import { toZonedTime } from 'date-fns-tz';

/**
 * UK wall-clock frame utilities (PCH-117).
 *
 * The quote form's client-side Dates are FRAME Dates: carriers of UK
 * wall-clock values in their browser-LOCAL fields. Their underlying UTC
 * instant is meaningless on non-UK browsers, so instant APIs
 * (`toISOString`, `formatInTimeZone`, `fromZonedTime`, `getTimezoneOffset`
 * arithmetic) must never touch them — the sole exception is the QuoteForm
 * submission boundary, which converts the frame back to a true instant.
 *
 * Everything in this module reads and writes LOCAL fields only, so it is
 * correct in every browser timezone. "Now" may only enter the frame via
 * `ukNow()`.
 */

const UK_TZ = 'Europe/London';

const MS_PER_DAY = 86400000;

/**
 * Strip invisible Unicode characters (U+200B–U+200D, U+FEFF) that Sanity
 * string data may contain, then trim. Same class of defect as
 * `normalizeClosingTime` in DatePicker.tsx.
 */
const cleanCmsString = (value: string): string =>
    value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();

/**
 * The current UK wall clock as a frame Date: its LOCAL fields hold the
 * current Europe/London date and time, whatever the browser timezone.
 * On a UK browser this is indistinguishable from `new Date()`, so UK
 * behaviour is unchanged by construction.
 *
 * Known limitation: if the browser's own DST spring-forward gap coincides
 * with the current UK wall-clock time, that hour is unrepresentable as a
 * local Date (≤1h skew, ~1h/year in affected zones).
 */
export const ukNow = (): Date => toZonedTime(new Date(), UK_TZ);

/**
 * Parse a UK calendar-date string into a frame Date at the given local hour.
 * Accepts both CMS ISO ('yyyy-MM-dd') and locale ('dd/mm/yyyy') forms and
 * strips zero-width characters before parsing.
 *
 * Never use `new Date('yyyy-MM-dd')` for this: the ES spec parses it as UTC
 * midnight, which is the PREVIOUS local day at negative offsets. Locale
 * strings fare worse — `new Date('dd/mm/yyyy')` parses as M/D/Y.
 */
export const parseUkDateString = (dateStr: string, hour = 0): Date => {
    const cleaned = cleanCmsString(dateStr);

    if (cleaned.includes('/')) {
        const [d, m, y] = cleaned.split('/').map(Number);
        return new Date(y, m - 1, d, hour);
    }

    const [y, m, d] = cleaned.split('-').map(Number);
    return new Date(y, m - 1, d, hour);
};

/**
 * Format a frame Date as 'yyyy-MM-dd' from its LOCAL fields — never UTC.
 * This is the frame counterpart of the banned `toISOString().split('T')[0]`.
 */
export const formatUkDateISO = (date: Date): string =>
    [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0'),
    ].join('-');

/**
 * Normalize a UK date string to 'dd/mm/yyyy' as a pure string operation —
 * no Date object, so no timezone can interfere. Accepts 'yyyy-MM-dd' (CMS)
 * and 'dd/mm/yyyy' (already locale — passed through zero-padded, day-first).
 * Strips zero-width characters (input is typically raw CMS data).
 */
export const ukIsoToLocale = (isoStr: string): string => {
    const cleaned = cleanCmsString(isoStr);

    if (cleaned.includes('/')) {
        const [d, m, y] = cleaned.split('/');
        return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }

    const [y, m, d] = cleaned.split('-');
    return `${d}/${m}/${y}`;
};

const utcFromLocalFields = (date: Date): number =>
    Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );

/**
 * Elapsed days between two frame Dates, rounded up — the chargeable-days
 * measure. Diffs the LOCAL fields on a fixed 24h day, so a DST transition
 * mid-rental cannot drift the count (the instant-based `getTime()` diff
 * gains or loses an hour across a clock change).
 *
 * Distinct semantics from `dateUtils.daysBetween` (calendar-day difference,
 * time-of-day ignored) — never merge the two.
 */
export const elapsedDaysCeil = (start: Date, end: Date): number =>
    Math.ceil((utcFromLocalFields(end) - utcFromLocalFields(start)) / MS_PER_DAY);
